<template>
  <div>
    <v-row>
      <v-col cols="2">
        <div class="overline">Yrke</div>
        <div
          v-if="professionObj"
          class="body-1"
        >{{ professionObj.text }}</div>
      </v-col>
      <v-col cols="2">
        <template v-if="hasProfessionOrientations(selectedApplication.professionId)">
          <div class="overline">Inriktning</div>
          <div class="body-1">{{ professionOrientationsSelected }}</div>
        </template>
      </v-col>
      <v-col cols="2">
        <div class="overline">Handledare</div>
        <div
          class="body-1"
          v-if="applicationEmployee"
        >
          <router-link :to="'/people/' + applicationEmployee.id">
            {{ applicationEmployee.firstName + ' ' + applicationEmployee.lastName }}
          </router-link>
        </div>
      </v-col>
      <v-col cols="2">
        <div class="overline">Handledarens yrkesbevis</div>
        <div
          class="body-1"
          v-if="applicationEmployee"
        >
          <div
            v-for="(cert, i) in applicationEmployee.tradeCertificates"
            :key="i"
            class
          >
            <div v-if="cert.profession && cert.profession.name">
              {{ cert.profession.name }}
            </div>
          </div>
          <div
            v-for="(exemp, i) in instructorExemptions"
            :key="`exemp-${i}`"
            class
          >
            <div>
              {{ exemp }}
            </div>
          </div>
        </div>
      </v-col>
      <v-col cols="2">
        <div class="overline">Arbetsplats</div>
        <div
          v-if="applicationWorkplace"
          class="body-1"
        >
          {{ get(applicationWorkplace, 'name', '') }} ({{ get(applicationWorkplace, 'postalZip', '') }},
          {{ get(applicationWorkplace, 'postalCity', '') }})
        </div>
      </v-col>
      <v-col cols="2">
        <template v-if="applicationCompany.hasDepartments">
          <div class="overline">Avdelning</div>
          <div v-if="applicationDepartment && applicationDepartment.name">
            <div class="body-1">
              {{ applicationDepartment.name }}
            </div>
          </div>
        </template>
      </v-col>
    </v-row>

    <MessageBox
      type="alert"
      class="mb-2"
      v-if="!instructorHaveCertificate"
    >
      <h6>VAR VÄNLIG KONTROLLERA ATT HANDLEDAREN MÖTER KRAVEN FÖR ATT HANDLEDA UNDER GRUNDUTBILDNING</h6>
      <p>
        Den utsedda handledaren möter kraven för att handleda denna lärling under kvalificeringstiden men det är inte
        säkert att hen möter kraven för att handleda under grundutbildningen. Var vänlig kontrollera att handledaren
        har de meriter och behörigheter som krävs för grundutbildning.
      </p>
    </MessageBox>

    <v-row>
      <v-col cols="3">
        <div class="overline">Anställningsform</div>
        <div class="body-1">{{ getEmploymentTypeNameById(Number(selectedApplication.employmentTypeId)) }}</div>
      </v-col>
      <v-col cols="3">
        <div class="overline">LÄRLINGSTIDENS STARTDATUM</div>
        <div class="body-1">{{ selectedApplication.employmentDate | date }}</div>
      </v-col>
    </v-row>

    <MessageBox class="mb-2 mt-n5">
      <h6>LÄRLINGSTIDENS STARTDATUM GÄLLER FÖR EVENTUELL GRUNDUTBILDNING</h6>
      <p>
        Detta datum gäller enbart grundutbildningen. Om lärlingens bedöms ha meriterna som krävs för kvalificeringstid
        kan detta datum ändras i efterhand för att bättre spegla det datum när personen faktiskt påbörjade sin tid som
        lärling.
      </p>
    </MessageBox>

    <v-row v-if="application.transferOldProfession && isUnderGU">
      <v-col>
        <v-btn
          :disabled="disabledButton"
          color="primary"
          @click="approveTransfer()"
        >
          <v-icon>$sl-notes-paper-approve</v-icon>GODKÄNN FÖRFLYTTNING
        </v-btn>
        <v-btn v-if="canUndoApprove" class="ml-3" @click="undoApproveTransfer()"><v-icon>$sl-cancel</v-icon>Ångra</v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import get from 'lodash/get'
import filter from 'lodash/filter'
import {InstructorAbilities, ApplicationProfession, UserRules} from '@/components/mixins'
import MessageBox from '@/components/common/MessageBox.vue'
import API from '@/services/api'

export default {
  name: 'ApplicationProfessionView',

  props: {
    professionObj: {
      type: Object,
      required: true
    },

    application: {
      type: Object,
      required: true
    },
    professionOrientationsArr: {
      type: Array,
    },
    professionOrientationsSelected: {
      type: String,
    }
  },

  mixins: [InstructorAbilities, ApplicationProfession, UserRules],

  components: {
    MessageBox
  },

  computed: {
    ...mapGetters('currentuser', ['currentUser']),

    instructorExemptions() {
      const iE = filter(get(this, 'applicationEmployee.instructorExemptions', []), o => o.approvedById !== null)
      if (iE) {
        return iE.map(o => {
          return `${get(this.getProfessionById(o.professionId), 'name', 'Yrke')} (Dispens)`
        })
      }
      return []
    },

    disabledButton() {
      const approvedById = this.selectedApplication?.transferApprovedById
      const regionId = this.selectedApplication?.regionId
      const isTransferOldProfessionApproved = this.selectedApplication?.transferOldProfessionApproved

      return !this.canUserApprove(regionId, approvedById) || isTransferOldProfessionApproved
    },

    isUnderGU() {
      return this.selectedApplication.education?.type === 'DistanceEducation'
    },

    canUndoApprove(){
      return this.selectedApplication?.transferApprovedById === this.currentUser.id && !this.selectedApplication?.transferApprovedBySecondaryId
    }
  },

  methods: {
    get,

    async undoApproveTransfer(){
      await API.updateApplication(this.selectedApplication.id, { 
        data: { 
          shouldUpdateTransferApplication: true,
          undoFirstApproval: true,
        } 
      })
      return this.$emit('saved')
    },

    async approveTransfer() {
      if ( !this.selectedApplication?.transferOldProfession ) return

      const res = await this.$dialog.confirm({
        text: "Du godkänner härmed flytt till nytt företag.",
        title: "Vill du godkänna flytt till nytt företag?",
        width: "550",
        actions: [
          { text: "Avbryt", key: false, icon: "$sl-cancel" },
          { text: "Godkänn", key: true, icon: "$sl-approve", outlined: true },
        ],
      });

      if (res !== true) return;

      if ( !this.selectedApplication?.transferApprovedById ) {
        await API.updateApplication(this.selectedApplication.id, { data: { shouldUpdateTransferApplication: true, transferApprovedById: this.currentUser.id } })
        return this.$emit('saved')
      }

      const { apprenticeship } = this.selectedApplication?.education

      if( !apprenticeship || !this.selectedApplication?.personId ) return

      //- Declare data
      const data = {
        employmentTypeId: this.selectedApplication?.employmentTypeId,
        companyId: this.selectedApplication?.companyId,
        instructorId: this.selectedApplication?.instructorId,
        personId: this.selectedApplication?.personId,
        professionId: this.selectedApplication?.professionId,
        toDate: new Date(),
        fromDate: this.selectedApplication?.employmentDate,
        workplaceIds: [this.selectedApplication?.workplaceId]
      }

      try {
        await API.moveApprenticeship(apprenticeship?.id, this.selectedApplication?.companyId, { data: data })
        return API.updateApplication(this.selectedApplication.id, { data: { 
            transferApprovedBySecondaryId: this.currentUser.id, 
            transferOldProfessionApproved: true,
            shouldUpdateTransferApplication: true 
          } 
        })
        .then(() => {
          this.$events.fire('showInfo', 'Flytt till nytt företag genomfört.') 
          return this.$emit('saved')
        })
      } catch (error) {
        this.$events.fire('showError', 'Flytt till nytt företag kunde tyvärr inte genomföras.') 
        console.log('error :: ', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
